import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import {
  client,
  createWoundAssessment,
  deleteWoundAssessment,
  deleteWoundAttachment,
  getWoundAssessments,
  getWoundAttachmentUrl,
  getWoundAttachments,
  updateWoundAssessment,
  uploadWoundAttachment,
} from '../../services/api';
import type {
  TaskListResponse,
  WoundAssessmentCreate,
  WoundAssessmentResponse,
  WoundAttachmentResponse,
  WoundResponse,
} from '../../services/api/types.gen';
import { getWoundTypeLabel } from '../../constants/wounds';
import { useStore } from '../../store';
import WoundAssessmentDialog from './WoundAssessmentDialog';
import WoundAssessmentCard from './WoundAssessmentCard';
import DeleteAssessmentDialog from './DeleteAssessmentDialog';

interface WoundAssessmentsDrawerProps {
  wound: WoundResponse | null;
  open: boolean;
  readOnly?: boolean;
  visitId?: string;
  visits?: TaskListResponse[];
  onOpenChange: (open: boolean) => void;
}

const WoundAssessmentsDrawer: React.FC<WoundAssessmentsDrawerProps> = ({
  wound,
  open,
  readOnly = false,
  visitId,
  visits = [],
  onOpenChange,
}) => {
  const [assessments, setAssessments] = useState<WoundAssessmentResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isAssessmentDialogOpen, setIsAssessmentDialogOpen] = useState(false);
  const [editingAssessment, setEditingAssessment] =
    useState<WoundAssessmentResponse | null>(null);
  const [previousAssessment, setPreviousAssessment] =
    useState<WoundAssessmentResponse | null>(null);

  const [deletingAssessment, setDeletingAssessment] =
    useState<WoundAssessmentResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Attachments (all for this wound, grouped by assessment_id in the UI)
  const [attachments, setAttachments] = useState<WoundAttachmentResponse[]>([]);
  const [uploadingForAssessment, setUploadingForAssessment] = useState<
    string | null
  >(null);

  const showError = useStore(state => state.showError);

  const fetchAssessments = useCallback(async () => {
    if (!wound) return;
    setIsLoading(true);
    setError(null);
    try {
      const resp = await getWoundAssessments({
        path: { patient_id: wound.patient_id, wound_id: wound.id },
        client,
      });
      setAssessments((resp.data as WoundAssessmentResponse[]) ?? []);
    } catch {
      setError('Failed to load assessments');
    } finally {
      setIsLoading(false);
    }
  }, [wound]);

  const fetchAttachments = useCallback(async () => {
    if (!wound) return;
    try {
      const resp = await getWoundAttachments({
        path: { patient_id: wound.patient_id, wound_id: wound.id },
        client,
      });
      setAttachments((resp.data as WoundAttachmentResponse[]) ?? []);
    } catch {
      // silently ignore — not critical
    }
  }, [wound]);

  useEffect(() => {
    if (open && wound) {
      fetchAssessments();
      fetchAttachments();
    } else {
      setAssessments([]);
      setAttachments([]);
      setError(null);
    }
  }, [open, wound, fetchAssessments, fetchAttachments]);

  const handleUploadAttachment = async (assessmentId: string, file: File) => {
    if (!wound) return;
    setUploadingForAssessment(assessmentId);
    try {
      await uploadWoundAttachment({
        path: { patient_id: wound.patient_id, wound_id: wound.id },
        query: { assessment_id: assessmentId },
        body: { file },
        client,
      });
      await fetchAttachments();
    } catch (err) {
      console.error('Failed to upload attachment:', err);
      showError('Failed to upload attachment. Please try again.');
    } finally {
      setUploadingForAssessment(null);
    }
  };

  const handleDownloadAttachment = async (
    attachment: WoundAttachmentResponse
  ) => {
    if (!wound) return;
    try {
      const resp = await getWoundAttachmentUrl({
        path: {
          patient_id: wound.patient_id,
          wound_id: wound.id,
          attachment_id: attachment.id,
        },
        client,
      });
      const data = resp.data as { url?: string };
      if (data?.url) window.open(data.url, '_blank');
    } catch (err) {
      console.error('Failed to download attachment:', err);
      showError('Failed to download attachment. Please try again.');
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!wound) return;
    try {
      await deleteWoundAttachment({
        path: {
          patient_id: wound.patient_id,
          wound_id: wound.id,
          attachment_id: attachmentId,
        },
        client,
      });
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      showError('Failed to delete attachment. Please try again.');
    }
  };

  const handleSaveAssessment = async (data: WoundAssessmentCreate) => {
    if (!wound) return;
    try {
      if (editingAssessment) {
        await updateWoundAssessment({
          path: {
            patient_id: wound.patient_id,
            wound_id: wound.id,
            assessment_id: editingAssessment.id,
          },
          body: data,
          client,
        });
      } else {
        await createWoundAssessment({
          path: { patient_id: wound.patient_id, wound_id: wound.id },
          body: data,
          client,
        });
      }
      setIsAssessmentDialogOpen(false);
      setEditingAssessment(null);
      setPreviousAssessment(null);
      fetchAssessments();
    } catch (err) {
      console.error('Failed to save assessment:', err);
      showError(
        editingAssessment
          ? 'Failed to update assessment. Please try again.'
          : 'Failed to save assessment. Please try again.'
      );
    }
  };

  const handleDeleteAssessment = async () => {
    if (!wound || !deletingAssessment) return;
    setIsDeleting(true);
    try {
      await deleteWoundAssessment({
        path: {
          patient_id: wound.patient_id,
          wound_id: wound.id,
          assessment_id: deletingAssessment.id,
        },
        client,
      });
      setDeletingAssessment(null);
      fetchAssessments();
    } catch (err) {
      console.error('Failed to delete assessment:', err);
      showError('Failed to delete assessment. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='w-[min(920px,92vw)] max-w-[920px] max-h-[85vh] overflow-y-auto'>
          <div className='mb-3 flex items-center justify-between border-b pb-3 pr-8'>
            <DialogHeader className='space-y-0'>
              <DialogTitle className='text-base'>
                {wound
                  ? `${wound.location} — ${getWoundTypeLabel(wound.wound_type)}`
                  : 'Assessments'}
              </DialogTitle>
            </DialogHeader>
            {!readOnly && (
              <div className='shrink-0'>
                <Button
                  size='sm'
                  onClick={() => {
                    setEditingAssessment(null);
                    setPreviousAssessment(null);
                    setIsAssessmentDialogOpen(true);
                  }}
                >
                  <Plus className='h-4 w-4 mr-1' />
                  Add Assessment
                </Button>
              </div>
            )}
          </div>

          <div className='space-y-3'>
            {isLoading ? (
              <div className='flex justify-center py-8'>
                <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
              </div>
            ) : error ? (
              <p className='text-sm text-destructive text-center py-4'>
                {error}
              </p>
            ) : assessments.length === 0 ? (
              <p className='text-sm text-muted-foreground text-center py-8'>
                No assessments recorded yet
              </p>
            ) : (
              <div className='grid gap-3 md:grid-cols-2'>
                {assessments.map(assessment => (
                  <WoundAssessmentCard
                    key={assessment.id}
                    assessment={assessment}
                    attachments={attachments.filter(
                      a => a.assessment_id === assessment.id
                    )}
                    readOnly={readOnly}
                    visits={visits}
                    isUploadingAttachment={
                      uploadingForAssessment === assessment.id
                    }
                    onEdit={() => {
                      setEditingAssessment(assessment);
                      setPreviousAssessment(null);
                      setIsAssessmentDialogOpen(true);
                    }}
                    onDelete={() => setDeletingAssessment(assessment)}
                    onUploadAttachment={file =>
                      handleUploadAttachment(assessment.id, file)
                    }
                    onDownloadAttachment={handleDownloadAttachment}
                    onDeleteAttachment={handleDeleteAttachment}
                  />
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assessment form dialog */}
      {wound && (
        <WoundAssessmentDialog
          open={isAssessmentDialogOpen}
          onOpenChange={setIsAssessmentDialogOpen}
          woundId={wound.id}
          visitId={visitId}
          editingAssessment={editingAssessment}
          previousAssessment={previousAssessment}
          onSave={handleSaveAssessment}
        />
      )}

      <DeleteAssessmentDialog
        open={!!deletingAssessment}
        isDeleting={isDeleting}
        onConfirm={handleDeleteAssessment}
        onClose={() => setDeletingAssessment(null)}
      />
    </>
  );
};

export default WoundAssessmentsDrawer;
